import React, { useState, useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Modal } from "reactstrap";

import AlertConfirmPopup from "components/common/AlertConfirmPopup";

import folderIcon from "styles/common/img/aicenter/ico-folder.svg";
import chartIcon from "styles/common/img/aicenter/ico-chart.svg";
import layersIcon from "styles/common/img/aicenter/ico-layers.svg";
import arrowOn from "styles/common/img/aicenter/ico_arrow_on.svg";
import arrowOff from "styles/common/img/aicenter/ico_arrow_off.svg";
import closeIcon from "styles/common/img/aicenter/ico_popup_close.png";
import titleImg from "styles/common/img/aicenter/ai-math-popup-title.png";
import ctaImg from "styles/common/img/aicenter/ai-math-popup-btn.png";
import labelSkill from "styles/common/img/aicenter/실력진단.png";
import labelAnalysis from "styles/common/img/aicenter/데이터분석.png";
import labelApply from "styles/common/img/aicenter/맞춤적용.png";

import "styles/common/css/aiMathStepper.css";

/* 팝업 내 이미지 — 열기 전 프리로드용 (순차 로드로 인한 깜빡임 방지) */
const AI_MATH_EXAM_START_IMAGES = [
    titleImg,
    ctaImg,
    labelSkill,
    labelAnalysis,
    labelApply,
    folderIcon,
    chartIcon,
    layersIcon,
    arrowOn,
    arrowOff,
    closeIcon,
];

// 모든 이미지의 로드+디코딩 완료를 기다린다. (실패해도 resolve 하여 멈추지 않음)
export const preloadAiMathExamStartImages = () =>
    Promise.all(
        AI_MATH_EXAM_START_IMAGES.map(
            (src) =>
                new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => (img.decode ? img.decode().then(resolve, resolve) : resolve());
                    img.onerror = resolve;
                    img.src = src;
                }),
        ),
    );

const STEPS = [
    { theme: "ams-css-build", icon: folderIcon, label: "실력 진단", labelImg: labelSkill },
    { theme: "ams-css-build ams-s-blue", icon: chartIcon, label: "데이터 분석", labelImg: labelAnalysis },
    { theme: "ams-css-build ams-s-purple", icon: layersIcon, label: "맞춤 적용", labelImg: labelApply },
];

const STEP_MS = 1500;
const SCENE_WIDTH = 912;

/* 원본 stardust 생성 로직 (22개) */
const makeStardust = () =>
    Array.from({ length: 22 }, () => ({
        left: Math.random() * 100,
        top: Math.random() * 100,
        delay: Number((Math.random() * 4).toFixed(2)),
        opacity: Number((0.15 + Math.random() * 0.35).toFixed(2)),
        scale: Number((0.5 + Math.random() * 1.2).toFixed(2)),
    }));

/* 원본 seedSparkles 로직 (step당 16개, 216px 원 중심 cx=cy=108) */
const makeSparkles = () => {
    const cx = 108;
    const cy = 108;
    return Array.from({ length: 16 }, () => {
        const ang = Math.random() * Math.PI * 2;
        const r = 36 + Math.random() * 70;
        const s = 0.5 + Math.random() * 1.1;
        return {
            x: cx + Math.cos(ang) * r,
            y: cy + Math.sin(ang) * r * 0.92,
            size: Number((4 + s * 4).toFixed(1)),
            tw: Number((1.1 + Math.random() * 1.4).toFixed(2)),
            td: Number((Math.random() * 1.6).toFixed(2)),
        };
    });
};

const AiMathExamStartPopup = ({ isOpen, onClose, onStart, mmbrSeq, gradCd, smstCd, examSeq }) => {
    const history = useHistory();
    const [active, setActive] = useState(0);
    const [scale, setScale] = useState(1);
    const sceneRef = useRef(null);

    /* 별먼지 / 스파클 좌표 — 마운트 시 1회 생성 */
    const stardust = useMemo(makeStardust, []);
    const sparkles = useMemo(() => STEPS.map(makeSparkles), []);

    /* 스텝 자동 순회 (원본 setInterval) */
    useEffect(() => {
        if (!isOpen) return undefined;
        setActive(0);
        const id = setInterval(() => {
            setActive((prev) => (prev + 1) % STEPS.length);
        }, STEP_MS);
        return () => clearInterval(id);
    }, [isOpen]);

    /* 화면 폭에 맞춰 912px 씬을 비례 축소 (원본 fit) */
    useEffect(() => {
        if (!isOpen) return undefined;
        const fit = () => {
            const avail = Math.min(window.innerWidth - 48, SCENE_WIDTH);
            setScale(Math.min(1, avail / SCENE_WIDTH));
        };
        fit();
        window.addEventListener("resize", fit);
        return () => window.removeEventListener("resize", fit);
    }, [isOpen]);

    /* 시작하기 — 응답값으로 AiPlayer 진단평가 화면으로 이동 (FromInit=1: 이탈 시 메인 복귀용 마커) */
    const handleClickStart = () => {
        if (typeof onStart === "function") onStart();
        history.push(
            `/AiPlayer?PLAYER_TYPE=APPLY&STD_TY_DVSN_CD=AI_ABLITY_STD&MMBR_SEQ=${mmbrSeq}` +
                `&GRAD_CD=${gradCd}&SMST_CD=${smstCd}&EXAM_SEQ=${examSeq}&IS_MATH_NEW=true&FromInit=1`,
        );
    };

    /* 닫기(X) — 안내 알럿 노출 후 확인 시 메인으로 이동 */
    const handleClickClose = async () => {
        await AlertConfirmPopup({
            type: "ALERT",
            msg: "오늘의 학습카드 및 AI학습관을 통해서도<br/>AI 수학 진단평가를 진행할 수 있습니다.",
        });
        history.replace("/today/middleStudyToday");
    };

    return (
        <Modal isOpen={isOpen} modalClassName="ai-math-stepper-modal" fade={false}>
            <div className="ai-math-stepper">
                {/* 좌상단 닫기 버튼 */}
                <button type="button" className="ai-math-stepper__close" onClick={handleClickClose}>
                    <img src={closeIcon} alt="닫기" />
                </button>

                {/* 배경 레이어 */}
                <div className="ams-bg" />

                {/* 콘텐츠: 타이틀 / 스테퍼 / 하단 버튼·문구 */}
                <div className="ams-layout">
                    {/* 상단 타이틀 (이미지) */}
                    <img
                        className="ams-title"
                        src={titleImg}
                        alt="성적 향상의 시작 — AI수학 진단평가를 통해 나에게 최적화된 표준 시간표로 학습을 시작하세요!"
                    />

                    {/* 스테퍼 */}
                    <div className="ams-stage-wrap">
                        <div className="ams-scene" ref={sceneRef} style={{ transform: `scale(${scale})` }}>
                            {/* 궤도: 스텝 중심을 통과하는 타원 (스텝보다 뒤) */}
                            <svg
                                className="ams-orbit-svg"
                                viewBox="0 0 912 258"
                                preserveAspectRatio="xMidYMid meet"
                                aria-hidden="true"
                            >
                                <defs>
                                    <linearGradient id="ams-ringGrad" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor="#4dd6ff" />
                                        <stop offset="50%" stopColor="#7aa8ff" />
                                        <stop offset="100%" stopColor="#a06bff" />
                                    </linearGradient>
                                    <radialGradient id="ams-dotGrad">
                                        <stop offset="0%" stopColor="#ffffff" />
                                        <stop offset="40%" stopColor="#cfe4ff" />
                                        <stop offset="100%" stopColor="#7aa8ff" />
                                    </radialGradient>
                                    <filter id="ams-soft" x="-80%" y="-80%" width="260%" height="260%">
                                        <feGaussianBlur stdDeviation="6" />
                                    </filter>
                                    <filter id="ams-glow" x="-200%" y="-200%" width="500%" height="500%">
                                        <feGaussianBlur stdDeviation="5" result="b" />
                                        <feMerge>
                                            <feMergeNode in="b" />
                                            <feMergeNode in="SourceGraphic" />
                                        </feMerge>
                                    </filter>
                                    <path id="ams-path1" d="M -348,0 a 348,118 0 1,1 696,0 a 348,118 0 1,1 -696,0" />
                                    <path id="ams-path2" d="M -348,0 a 348,118 0 1,1 696,0 a 348,118 0 1,1 -696,0" />
                                </defs>

                                <g transform="translate(456,108)">
                                    {/* 밝은 링 */}
                                    <g transform="rotate(4)">
                                        <use
                                            href="#ams-path1"
                                            fill="none"
                                            stroke="url(#ams-ringGrad)"
                                            strokeWidth="2.4"
                                            opacity="0.9"
                                            filter="url(#ams-glow)"
                                        />
                                        <use
                                            href="#ams-path1"
                                            fill="none"
                                            stroke="url(#ams-ringGrad)"
                                            strokeWidth="1"
                                            opacity="0.6"
                                        />
                                        <circle
                                            r="5.5"
                                            fill="url(#ams-dotGrad)"
                                            filter="url(#ams-glow)"
                                            className="ams-moving"
                                        >
                                            <animateMotion dur="9s" repeatCount="indefinite" rotate="auto">
                                                <mpath href="#ams-path1" />
                                            </animateMotion>
                                        </circle>
                                        <circle
                                            r="3.5"
                                            fill="url(#ams-dotGrad)"
                                            filter="url(#ams-glow)"
                                            className="ams-moving"
                                        >
                                            <animateMotion dur="9s" begin="-3s" repeatCount="indefinite">
                                                <mpath href="#ams-path1" />
                                            </animateMotion>
                                        </circle>
                                        <circle
                                            r="3"
                                            fill="url(#ams-dotGrad)"
                                            filter="url(#ams-glow)"
                                            className="ams-moving"
                                        >
                                            <animateMotion dur="9s" begin="-6s" repeatCount="indefinite">
                                                <mpath href="#ams-path1" />
                                            </animateMotion>
                                        </circle>
                                    </g>
                                    {/* 흐린 링 */}
                                    <g transform="rotate(-8)" opacity="0.55">
                                        <use
                                            href="#ams-path2"
                                            fill="none"
                                            stroke="#9fb4ff"
                                            strokeWidth="1.4"
                                            opacity="0.55"
                                            filter="url(#ams-soft)"
                                        />
                                        <use
                                            href="#ams-path2"
                                            fill="none"
                                            stroke="#c7d4ff"
                                            strokeWidth="0.8"
                                            opacity="0.5"
                                        />
                                        <circle
                                            r="4"
                                            fill="url(#ams-dotGrad)"
                                            filter="url(#ams-glow)"
                                            className="ams-moving"
                                        >
                                            <animateMotion dur="12s" repeatCount="indefinite">
                                                <mpath href="#ams-path2" />
                                            </animateMotion>
                                        </circle>
                                        <circle
                                            r="2.6"
                                            fill="url(#ams-dotGrad)"
                                            filter="url(#ams-glow)"
                                            className="ams-moving"
                                        >
                                            <animateMotion dur="12s" begin="-5s" repeatCount="indefinite">
                                                <mpath href="#ams-path2" />
                                            </animateMotion>
                                        </circle>
                                    </g>
                                </g>
                            </svg>

                            <div className="ams-stage">
                                {STEPS.map((step, i) => (
                                    <React.Fragment key={`step-${i}`}>
                                        <div className={`ams-step ${step.theme}${i === active ? " ams-active" : ""}`}>
                                            <div className="ams-circle">
                                                <div className="ams-layer ams-ring-off" />
                                                <div className="ams-layer ams-glow-outer" />
                                                <div className="ams-layer ams-glow-inner" />
                                                <div className="ams-layer ams-orb" />
                                                <div className="ams-spark">
                                                    {sparkles[i].map((b, bi) => (
                                                        <b
                                                            key={`spark-${i}-${bi}`}
                                                            style={{
                                                                left: `${b.x}px`,
                                                                top: `${b.y}px`,
                                                                width: `${b.size}px`,
                                                                height: `${b.size}px`,
                                                                "--tw": `${b.tw}s`,
                                                                "--td": `${b.td}s`,
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                                <img className="ams-ico-off" src={step.icon} alt={step.label} />
                                            </div>
                                            <img className="ams-label" src={step.labelImg} alt={step.label} />
                                        </div>

                                        {i < STEPS.length - 1 && (
                                            <div className={`ams-chev${i === active ? " ams-lit" : ""}`}>
                                                <img className="ams-on" src={arrowOn} alt="" />
                                                <img className="ams-off" src={arrowOff} alt="" />
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* 하단: CTA 버튼(이미지) + 상세 문구(코드) */}
                    <div className="ams-bottom">
                        <button type="button" className="ams-cta" onClick={handleClickStart}>
                            <img src={ctaImg} alt="AI수학 진단평가 시작하기" />
                        </button>

                        <ul className="ams-notes">
                            <li className="ams-notes__item">
                                <span className="ams-notes__hl">{"AI학습관 > AI수학 "}</span>
                                에서도 AI수학 진단평가를 응시할 수 있습니다.
                            </li>
                            <li className="ams-notes__item">
                                응시 완료 시 <span className="ams-notes__hl">기본 별 5개</span>가 지급 되며, 점수에 따라{" "}
                                <span className="ams-notes__hl">최대 10개의 별이 추가 지급</span>됩니다.
                            </li>
                            <li className="ams-notes__sub">
                                (추가 보상: 100점 10개, 80점 이상 8개, 60점 이상 6개 추가 지급)
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AiMathExamStartPopup;
